import React, { useEffect, useRef, useState } from 'react';
import { scrapingService } from '../services/scrapingService';
import { isTerminalWorkflow } from '../utils/v3LeadsHelpers';
import { OPERATIONS_KEY, JOBS_KEY, readStored, writeStored, operationIdFor,
  retryCandidates, groupItemsByJob } from '../utils/v3RecoveryHelpers';

const CONSENT = 'I understand these requests may have reached BatchData previously and authorize one additional paid enrichment attempt for the selected owners.';
const COUNT_LABELS = [
  ['historicalUncertain', 'Previous uncertain attempts'],
  ['currentInterrupted', 'Current interrupted attempts'],
  ['safeQueued', 'Safe queued targets'],
  ['activeLeases', 'Active leases (excluded)'],
  ['storedResponses', 'Stored response records'],
  ['savedResponses', 'Reusable response evidence'],
];

function attemptSignature(items) {
  return items.map((item) => `${item.jobItemId}:${item.recoveryOperationId || ''}:${(item.runIds || []).slice().sort().join(',')}`).sort().join('|');
}

const V3UncertainRecoveryPanel = ({ scope, currentJobId, onResultsAvailable, onChanged }) => {
  const [review, setReview] = useState(null);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [consent, setConsent] = useState(false);
  const [dryRun, setDryRun] = useState(null);
  const [reconcileConsent, setReconcileConsent] = useState(false);
  const [resumeConsent, setResumeConsent] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [knownJobIds, setKnownJobIds] = useState(() => readStored(JOBS_KEY, []));
  const [progress, setProgress] = useState({});
  const [progressError, setProgressError] = useState(null);
  const [progressVersion, setProgressVersion] = useState(0);
  const operations = useRef(readStored(OPERATIONS_KEY, {}));
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const generation = useRef(0);
  const completedNotified = useRef(new Set());
  const callbacks = useRef({ onResultsAvailable, onChanged });
  callbacks.current = { onResultsAvailable, onChanged };
  const scopeSignature = JSON.stringify(scope || {});

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    generation.current += 1;
    setReview(null); setOpen(false); setSelected([]); setConsent(false);
    setDryRun(null); setReconcileConsent(false); setError(null); setMessage(null);
  }, [scopeSignature, currentJobId]);

  useEffect(() => {
    if (!knownJobIds.length) return undefined;
    let active = true;
    let timer;
    async function observe() {
      const results = await Promise.allSettled(knownJobIds.map((id) => scrapingService.getV3LeadsJob(id)));
      if (!active) return;
      let continuePolling = false;
      const next = {};
      let readFailed = false;
      results.forEach((result, index) => {
        const id = knownJobIds[index];
        if (result.status === 'rejected') { readFailed = true; continuePolling = true; return; }
        const job = result.value;
        next[id] = job;
        if (!isTerminalWorkflow(job)) continuePolling = true;
        else if (!completedNotified.current.has(id)) {
          completedNotified.current.add(id);
          callbacks.current.onResultsAvailable?.(job);
        }
      });
      setProgress((last) => ({ ...last, ...next }));
      setProgressError(readFailed ? 'Some recovery status reads failed. Showing the last known progress and retrying observation.' : null);
      if (continuePolling) timer = setTimeout(observe, 4000);
    }
    observe();
    return () => { active = false; clearTimeout(timer); };
  }, [knownJobIds, progressVersion]);

  const rememberJobs = (ids) => {
    setKnownJobIds((previous) => {
      const next = [...new Set([...previous, ...ids.filter(Boolean)])];
      writeStored(JOBS_KEY, next);
      return next;
    });
    ids.forEach((id) => completedNotified.current.delete(id));
    setProgressVersion((version) => version + 1);
  };

  const loadReview = async () => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy('review'); setOpen(true); setError(null); setMessage(null);
    const requestGeneration = generation.current;
    try {
      const data = await scrapingService.reviewV3LeadsRecovery({ ...scope, currentJobId });
      if (!mounted.current || requestGeneration !== generation.current) return;
      setReview(data); setSelected([]); setConsent(false); setDryRun(null);
      setReconcileConsent(false); setResumeConsent(false);
    } catch (failure) { if (mounted.current) setError(failure.message); }
    finally { inFlight.current = false; if (mounted.current) setBusy(null); }
  };

  const candidates = retryCandidates(review?.items || []);
  const selectedItems = candidates.filter((item) => selected.includes(item.jobItemId));
  const reconcileItems = (review?.items || []).filter((item) => item.needsReconciliation && !item.activeLease);
  const pausedJobs = (review?.jobs || []).filter((job) => job.paidDispatchPaused && job.status !== 'cancelled');
  const estimatedRequestsMin = selectedItems.length;
  const estimatedRequestsMax = selectedItems.reduce((sum, item) => sum + (item.estimatedPaidRequestsMax || 2), 0);

  const reconcile = async (apply) => {
    if (inFlight.current || (apply && (!dryRun || !reconcileConsent))) return;
    inFlight.current = true; setBusy(apply ? 'reconcile' : 'dry-run'); setError(null); setMessage(null);
    try {
      const items = apply ? dryRun.items : reconcileItems;
      const results = [];
      for (const [jobId, jobItemIds] of Object.entries(groupItemsByJob(items))) {
        const operationId = operationIdFor(operations.current, `reconcile:${attemptSignature(items.filter((item) => item.jobId === jobId))}`, jobId, jobItemIds);
        results.push(await scrapingService.reconcileV3LeadsUncertain(jobId, {
          dryRun: !apply, operationId, jobItemIds, confirmReconciliation: apply === true,
        }));
        if (apply) rememberJobs([jobId]);
      }
      if (!mounted.current) return;
      if (apply) {
        setReview(null); setDryRun(null); setSelected([]); setConsent(false); setReconcileConsent(false);
        setMessage('Reconciliation recorded. Reload the review to select attempts that require paid retry confirmation. Stored responses are reused before any new request; a required mailing fallback may still be pending.');
        callbacks.current.onChanged?.();
      } else setDryRun({ items: [...items], results });
    } catch (failure) { if (mounted.current) setError(failure.message); }
    finally { inFlight.current = false; if (mounted.current) setBusy(null); }
  };

  const authorizeRetry = async () => {
    if (inFlight.current || !consent || !selectedItems.length) return;
    inFlight.current = true; setBusy('retry'); setError(null); setMessage(null);
    let authorizedCount = 0;
    try {
      for (const [jobId, jobItemIds] of Object.entries(groupItemsByJob(selectedItems))) {
        const operationId = operationIdFor(operations.current, `retry:${attemptSignature(selectedItems.filter((item) => item.jobId === jobId))}`, jobId, jobItemIds);
        const result = await scrapingService.confirmV3LeadsUncertainRetry(jobId, jobItemIds, true, operationId);
        authorizedCount += (result.requeuedCount ?? 0) + (result.resumedCount ?? 0);
        rememberJobs([jobId, result.recoveryJobId, result.jobId, ...(result.recoveryJobIds || [])]);
      }
      if (!mounted.current) return;
      setSelected([]); setConsent(false); setReview(null); setDryRun(null);
      setMessage(`Recovery authorization recorded for ${selectedItems.length} selected owners (${authorizedCount} queued or resumed). Paid dispatch remains subject to each job's pause and the worker setting.`);
      callbacks.current.onChanged?.();
    } catch (failure) {
      if (mounted.current) setError(`${failure.message} Retry this confirmation to reuse the same operation IDs. Some jobs may already have recorded authorization; they will not receive another authorization from this submission.`);
    } finally { inFlight.current = false; if (mounted.current) setBusy(null); }
  };

  const resumeDispatch = async () => {
    if (inFlight.current || !resumeConsent || !pausedJobs.length) return;
    inFlight.current = true; setBusy('resume'); setError(null); setMessage(null);
    try {
      for (const job of pausedJobs) {
        const operationId = operationIdFor(operations.current,
          `resume:${job.pausedAt || job.paidDispatchPausedAt || job.circuitOpenedAt || ''}`, job.jobId);
        await scrapingService.resumeV3LeadsDispatch(job.jobId, { operationId, confirmResume: true });
        rememberJobs([job.jobId]);
      }
      if (mounted.current) {
        setReview(null); setResumeConsent(false);
        setMessage('Paid dispatch resumed for the reviewed jobs. Processing still requires the worker to be enabled.');
        callbacks.current.onChanged?.();
      }
    } catch (failure) { if (mounted.current) setError(failure.message); }
    finally { inFlight.current = false; if (mounted.current) setBusy(null); }
  };

  const scopeReady = Array.isArray(scope?.countyIds) && scope.countyIds.length > 0;
  return <section className="mt-4 border border-amber-200 rounded-lg p-3 text-sm" aria-label="Uncertain BatchData recovery">
    <button type="button" className="px-3 py-2 border rounded text-amber-900 disabled:opacity-50"
      disabled={!!busy || !scopeReady} onClick={loadReview}>Review &amp; Retry Uncertain BatchData Attempts</button>
    {!scopeReady && <p className="mt-2 text-gray-600">Select counties to review uncertain attempts.</p>}
    {open && <div className="mt-3" aria-busy={!!busy}>
      <h4 className="font-semibold">Review uncertain BatchData attempts</h4>
      <p className="mt-1">This review covers the selected counties and acreage range, including earlier cancelled jobs. Reviewing makes no provider calls.</p>
      {currentJobId && <p className="mt-1 break-all">Current job: {currentJobId}</p>}
      <p className="text-gray-600">Acreage: {scope?.filterMode === 'atmax' ? 0 : scope?.minAcres ?? 0} to {scope?.filterMode === 'atleast' ? 'unlimited' : scope?.maxAcres ?? 'unlimited'}</p>
      {busy === 'review' && <p role="status" className="mt-2">Loading recovery review…</p>}
      {error && <p role="alert" className="mt-2 text-red-800">{error}</p>}
      {message && <p role="status" className="mt-2 text-teal-800">{message}</p>}
      {review && <>
        <dl className="grid grid-cols-2 gap-2 mt-3">{COUNT_LABELS.map(([key, label]) => <React.Fragment key={key}>
          <dt>{label}</dt><dd>{review.counts?.[key] ?? 0}</dd>
        </React.Fragment>)}</dl>
        <div className="overflow-x-auto mt-3"><table className="w-full text-left">
          <caption className="text-left font-medium">Counts by county</caption>
          <thead><tr><th>County</th>{COUNT_LABELS.map(([key, label]) => <th key={key} className="px-2">{label}</th>)}</tr></thead>
          <tbody>{(review.counties || []).map((county) => <tr key={county.countyId}>
            <td>{county.countyName || county.countyId}</td>{COUNT_LABELS.map(([key]) => <td className="px-2" key={key}>{county[key] ?? county.counts?.[key] ?? 0}</td>)}
          </tr>)}</tbody>
        </table></div>
        <ul className="mt-3 space-y-1" aria-label="Affected jobs">{(review.jobs || []).map((job) => <li key={job.jobId}>
          <span className="font-mono break-all">{job.jobId}</span> — {job.status}{job.paidDispatchPaused ? ' · Paid dispatch paused' : ''}
        </li>)}</ul>
        {reconcileItems.length > 0 && <div className="mt-4 p-3 border rounded">
          <p>{reconcileItems.length} expired or interrupted attempts need reconciliation. Active leases are excluded. Saved provider responses and completed outcomes are checked before any paid retry is offered.</p>
          <button type="button" disabled={!!busy} onClick={() => reconcile(false)} className="mt-2 border rounded px-3 py-2">Preview reconciliation (dry run)</button>
          {dryRun && <>
            <p className="mt-2">Dry run reviewed {dryRun.items.length} attempts across {dryRun.results.length} jobs. No records changed and no provider calls were made.</p>
            <p>{dryRun.results.reduce((sum, result) => sum + (result.resumedCount || 0), 0)} stored responses can resume;
              {' '}{dryRun.results.reduce((sum, result) => sum + (result.reusedCompletedCount || 0), 0)} completed outcomes can be reused;
              {' '}{dryRun.results.reduce((sum, result) => sum + (result.heldCount || 0), 0)} attempts will remain held for paid retry confirmation;
              {' '}{dryRun.results.reduce((sum, result) => sum + (result.notEligibleIds?.length || 0), 0)} are no longer eligible.</p>
            <ul className="mt-2">{dryRun.results.map((result, index) => <li key={result.jobId || index}>
              Job {result.jobId || Object.keys(groupItemsByJob(dryRun.items))[index]}: {result.resumedCount ?? 0} saved responses to resume, {result.reusedCompletedCount ?? 0} completed outcomes to reuse, {result.heldCount ?? 0} held for confirmation.
            </li>)}</ul>
            <label className="block mt-2"><input type="checkbox" checked={reconcileConsent} disabled={!!busy}
              onChange={(event) => setReconcileConsent(event.target.checked)} /> I authorize reconciliation of these expired attempts. Uncertain results will remain held for separate paid retry confirmation.</label>
            <button type="button" disabled={!!busy || !reconcileConsent} onClick={() => reconcile(true)} className="mt-2 border rounded px-3 py-2">Reconcile reviewed expired attempts</button>
          </>}
        </div>}
        <div className="mt-4">
          <label><input type="checkbox" aria-label="Select all eligible uncertain owners" disabled={!!busy || !candidates.length}
            checked={!!candidates.length && selectedItems.length === candidates.length}
            onChange={(event) => { setSelected(event.target.checked ? candidates.map((item) => item.jobItemId) : []); setConsent(false); }} /> Select all eligible uncertain owners ({candidates.length})</label>
          <div className="overflow-auto max-h-80 mt-2"><table className="w-full text-left">
            <thead><tr>{['Select', 'Owner ID', 'County', 'Original job', 'State', 'Stored response'].map((label) => <th key={label} className="p-2">{label}</th>)}</tr></thead>
            <tbody>{(review.items || []).map((item) => {
              const selectable = candidates.some((candidate) => candidate.jobItemId === item.jobItemId);
              return <tr key={item.jobItemId} className="border-t">
                <td className="p-2"><input type="checkbox" aria-label={`Select owner ${item.ownerUniqueId} from job ${item.jobId}`}
                  checked={selected.includes(item.jobItemId)} disabled={!!busy || !selectable}
                  onChange={(event) => { setSelected((ids) => event.target.checked ? [...ids, item.jobItemId] : ids.filter((id) => id !== item.jobItemId)); setConsent(false); }} /></td>
                <td className="p-2 break-all">{item.ownerUniqueId}</td><td className="p-2">{item.countyName || item.countyId}</td>
                <td className="p-2 break-all">{item.jobId}</td><td className="p-2">{item.activeLease ? 'Active lease — unavailable' : item.completedOutcome ? 'Completed outcome — reuse' : item.ownerConflict ? 'Reserved by other work — unavailable' : item.needsReconciliation ? 'Reconciliation required' : item.status}</td>
                <td className="p-2">{item.hasSavedResponse ? 'Available — reuse saved evidence' : item.hasStoredResponse ? 'Stored evidence requires review' : 'No saved response'}</td>
              </tr>;
            })}</tbody>
          </table></div>
          <p className="mt-3">Selected owners: {selectedItems.length}. Estimated possible new paid requests: {estimatedRequestsMin}–{estimatedRequestsMax}, including mailing-address fallback when needed. BatchData may charge again.</p>
          <label className="block mt-3 text-red-900"><input type="checkbox" checked={consent} disabled={!!busy || !selectedItems.length}
            onChange={(event) => setConsent(event.target.checked)} /> {CONSENT}</label>
          <button type="button" disabled={!!busy || !consent || !selectedItems.length} onClick={authorizeRetry}
            className="mt-2 px-3 py-2 border border-red-400 text-red-900 rounded disabled:opacity-50">{busy === 'retry' ? 'Recording authorization…' : 'Authorize selected uncertain retries'}</button>
        </div>
        {pausedJobs.length > 0 && <div className="mt-4 border rounded p-3">
          <p className="mb-2">Resuming reuses saved responses and can continue a pending authorized mailing fallback. Held owners still require separate retry confirmation.</p>
          <label className="block"><input type="checkbox" checked={resumeConsent} disabled={!!busy}
            onChange={(event) => setResumeConsent(event.target.checked)} /> I authorize resuming paid dispatch for the {pausedJobs.length} reviewed paused jobs, including their safe queued items and any explicitly authorized recovery items.</label>
          <button type="button" disabled={!!busy || !resumeConsent} onClick={resumeDispatch} className="mt-2 px-3 py-2 border rounded">Resume reviewed paused jobs</button>
        </div>}
      </>}
    </div>}
    {knownJobIds.length > 0 && <div className="mt-4" aria-label="Recovery progress">
      <h4 className="font-semibold">Recovery progress</h4>
      <p>These recovery jobs are tracked separately from the original scope progress.</p>
      {progressError && <p role="alert" className="text-amber-800">{progressError}</p>}
      <ul className="mt-2 space-y-2">{knownJobIds.map((id) => {
        const job = progress[id];
        return <li key={id} className="border rounded p-2"><span className="font-mono break-all">{id}</span>
          {job ? <span> — {job.status}; {job.recoveryAuthorizedCount != null && <span>{job.recoveryCompletedCount ?? 0} / {job.recoveryAuthorizedCount} authorized recovery owners completed; {job.recoveryQueuedCount ?? 0} recovery owners queued; {job.recoveryHeldCount ?? 0} recovery owners held. </span>}
            Original job totals: {job.completedItems ?? 0} completed / {job.totalItems ?? 0} owners; {job.queuedItems ?? 0} queued; {job.retryConfirmationRequiredCount ?? 0} held; {job.creditsUsed ?? 0} recorded credits{job.paidDispatchPaused ? '; paid dispatch paused' : ''}</span> : ' — Loading recovery status…'}
        </li>;
      })}</ul>
    </div>}
  </section>;
};

export default V3UncertainRecoveryPanel;
